import { ArpFields } from "constants/forecasts.constants";
import { utc } from "dayjs";

import { ArpSegment } from "../../../models/UserArpsModel";
import { EPSILON, areApproximatelyEqual, roundTo } from "../../../utils/numbers";
import { CumulativeData } from "../../forecasting/Forecasting";
import { ProductTypeEnumAllCaps } from "../models/ArpsSegment";
import { DEFAULT_TFLAT } from "./arpsConstants";
import {
  convertDeclineToNominal,
  convertNominalToDeclineType,
  getDiAtDayFromQiToQf,
  isConstrainedSegment,
  isRampUpSegment
} from "./arpsUtils";
import {
  SECONDS_IN_DAY,
  addAvgFractionalMonths,
  avgMonthsDiff,
  daysDiff,
  getDateStringAtMidnightUtc,
  getDurationInDays,
  getDurationInMonths
} from "./dates";
import { arpsWasm } from "./wasmLoader";

const RATE_DECIMAL_PLACES = 8;
const MAX_NUMBER_OF_DAYS = 3650000; //10000 years
export interface UpdateSegment {
  (params: {
    segmentTemplateName: string;
    productArps: ArpSegment[];
    declineType: string;
    header: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    val: any;
    cumulativeData?: CumulativeData;
  }): ArpSegment[];
}

export const updateSessionDf = ({
  key,
  product,
  newDf
}: {
  key: string;
  product: string;
  newDf: number;
}): void => {
  if (!key) {
    return;
  }
  sessionStorage.setItem(`well_df_${key}_${product}`, newDf?.toString());
};

export function removeSessionDf({
  key,
  product
}: {
  key: string;
  product: string;
}): void {
  const storageKey = `well_df_${key}_${product}`;
  sessionStorage.removeItem(storageKey);
}

function updateEndDates(segments: ArpSegment[]) {
  if (!arpsWasm) {
    throw "arpsWasm is undefined";
  }
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const startDate = utc(seg.startDate);
    const day = Math.min(
      MAX_NUMBER_OF_DAYS,
      arpsWasm.getDayAtRate(seg.qi, seg.di, seg.b, seg.qf)
    );

    if (!isFinite(day) || areApproximatelyEqual(day, 0, EPSILON)) {
      // This will be caught by the validation
      continue;
    }

    const totalSeconds = Math.round(day * SECONDS_IN_DAY);
    const endDate = startDate.add(totalSeconds, "seconds");

    seg.endDate = endDate.toISOString();
    if (i + 1 < segments.length) {
      const next = segments[i + 1];
      next.startDate = seg.endDate;
    }
  }
}

function updateTrendCum(segments: ArpSegment[]) {
  if (!arpsWasm) {
    throw "arpsWasm is undefined";
  }
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (seg.endDate && seg.startDate) {
      seg.trendCum = arpsWasm.getSegmentVolume(
        seg.qi,
        seg.di,
        seg.b,
        seg.qf,
        BigInt(Math.round(new Date(seg.startDate).getTime() / 1000)),
        BigInt(Math.round(new Date(seg.endDate).getTime() / 1000))
      );
    } else {
      seg.trendCum = 0;
    }
  }
}

/**
 * Updates the properties of a ramp-up segment based on its start and end dates.
 *
 * This function calculates the decline parameters for a ramp-up segment by:
 * 1. Computing the duration in days between the start and end dates
 * 2. Setting a minimum value for initial rate (qi) to avoid calculation errors
 * 3. Setting the b-factor to -1.0 (harmonic decline)
 * 4. Calculating the initial decline rate (di) based on the given flow rates and duration
 * 5. Calculating the final decline rate (df) based on the newly computed parameters
 *
 * @param segment - The ArpSegment object representing a ramp-up segment to be updated
 * @param isRampUp
 */
export const updateRampUpSegmentValues = (segment: ArpSegment, isRampUp: boolean) => {
  const day = daysDiff(segment.startDate, segment.endDate);
  if (segment.qi < EPSILON) {
    //qi zero is nan when calculating di
    segment.qi = 0.000001;
  }
  if (isRampUp) {
    if (areApproximatelyEqual(segment.qi, segment.qf, EPSILON)) {
      segment.b = 0;
      // Flat line. Cannot do exactly 0 because it causes calculation errors.
      segment.di = -1 * 1e-11;
    } else {
      segment.b = -1.0; //ramp segment is harmonic
      segment.di = arpsWasm.calculateDiAtDaysFromQiToQf(
        segment.qi,
        segment.qf,
        segment.b,
        day
      );
    }
  }
  segment.df = arpsWasm.getNominalDeclineAtRate(
    segment.di,
    segment.qi,
    segment.b,
    segment.qf
  );
};

export function updateSegmentsFromHeaderValues({
  header,
  segmentTemplateName,
  segments,
  declineType,
  val,
  cumulativeData
}: {
  header: string;
  segmentTemplateName: string;
  segments: ArpSegment[];
  declineType: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  val: any;
  cumulativeData?: CumulativeData;
}) {
  const hasRampUpSegment = isRampUpSegment({ segmentTemplateName, segments });
  const hasConstrainedSegment = isConstrainedSegment({ segmentTemplateName, segments });

  const hasInitialSegment = hasRampUpSegment || hasConstrainedSegment;

  const offset = hasInitialSegment ? 0 : -1;
  const seg0 = offset === 0 ? segments[0] : null;
  const seg1 = segments[offset + 1];
  const seg2 = segments[offset + 2];
  const seg3 = segments[offset + 3];
  const is2Segments =
    (!hasInitialSegment && segments.length === 2) ||
    (hasInitialSegment && segments.length === 3);
  if (header.startsWith("Q")) {
    // Rates cannot be negative.
    // Rate of 0 is also not allowed since we get errors if calculating with exactly 0.
    if (val < EPSILON) {
      val = 0.000001;
    }
  }
  if (header === "Q0" && seg0) {
    seg0.qi = val;
    // For ramp-up and constrained segments, always set the q0 field to preserve the value
    seg0.q0 = val;
    if ((seg0.qf < val && hasRampUpSegment) || (seg0.qf > val && hasConstrainedSegment)) {
      seg0.qf = val;
      seg1.qi = val;
    }
    if (hasRampUpSegment) {
      updateRampUpSegmentValues(seg0, hasRampUpSegment);
    } else if (hasConstrainedSegment) {
      // Recalculate di when Q0 changes for constrained segments
      // This handles the transition from flat (Q0 = Qi) to declining (Q0 > Qi)
      seg0.di = getDiAtDayFromQiToQf(
        seg0.qi,
        seg0.qf,
        seg0.b,
        getDurationInDays(seg0.startDate, seg0.endDate)
      );
      // Recalculate df based on the new di
      seg0.df = arpsWasm.getNominalDeclineAtRate(seg0.di, seg0.qi, seg0.b, seg0.qf);
    }
  }

  if (header === "Bcons") {
    seg0.b = val;
    seg0.di = getDiAtDayFromQiToQf(
      seg0.qi,
      seg0.qf,
      val,
      getDurationInDays(seg0.startDate, seg0.endDate)
    );
    // Recalculate df based on the new di and b
    seg0.df = arpsWasm.getNominalDeclineAtRate(seg0.di, seg0.qi, val, seg0.qf);
  }

  if (header === ArpFields.TCons) {
    seg0.endDate = addAvgFractionalMonths(seg0.startDate, val);
    seg1.startDate = seg0.endDate;
    const daysInBetween = getDurationInDays(seg0.startDate, seg0.endDate);
    seg0.di = getDiAtDayFromQiToQf(seg0.qi, seg0.qf, seg0.b, daysInBetween);
    // Recalculate df based on the new di
    seg0.df = arpsWasm.getNominalDeclineAtRate(seg0.di, seg0.qi, seg0.b, seg0.qf);

    updateEndDates(segments);
  }

  if (header === ArpFields.TRamp && seg0) {
    seg0.endDate = addAvgFractionalMonths(seg0.startDate, val);
    updateRampUpSegmentValues(seg0, hasRampUpSegment);
    seg1.startDate = seg0.endDate;
    updateEndDates(segments);
  }

  if (header === ArpFields.TTrans) {
    seg1.endDate = addAvgFractionalMonths(seg1.startDate, val);
  }

  if (header === "Qi") {
    if (seg0) {
      seg0.qf = val;

      if (seg0.qi > val && hasRampUpSegment) {
        seg0.qi = val;
      } else if (seg0.qi < val && hasConstrainedSegment) {
        // If Qi is raised above Q0, bump Q0 up to match
        seg0.qi = val;
        seg0.q0 = val;
      }

      if (hasRampUpSegment) {
        updateRampUpSegmentValues(seg0, hasRampUpSegment);
      } else if (hasConstrainedSegment) {
        // Recalculate di when Qi changes for constrained segments
        // This handles transitions between flat (Q0 = Qi) and declining (Q0 > Qi)
        seg0.di = getDiAtDayFromQiToQf(
          seg0.qi,
          seg0.qf,
          seg0.b,
          getDurationInDays(seg0.startDate, seg0.endDate)
        );
        // Recalculate df based on the new di
        seg0.df = arpsWasm.getNominalDeclineAtRate(seg0.di, seg0.qi, seg0.b, seg0.qf);
      }
    }

    seg1.qi = val;
    if (segments.length > 1) {
      //qi changed so qf needs to reflect that
      seg1.qf = roundTo(
        arpsWasm.getRateAtNominal(seg1.di, seg1.df, seg1.qi, seg1.b),
        RATE_DECIMAL_PLACES
      );
      if (seg2) {
        seg2.qi = seg1.qf;
      }
    }
  }

  if (header === "maxRate") {
    // Assumption: Max rate is always set to the same value for all segments.
    segments.forEach((segment: ArpSegment) => {
      if (segment) {
        segment.maxRate = val;
      }
    });
  }

  if (header.includes("Di")) {
    seg1.di = convertDeclineToNominal({
      declineValuePercent: val,
      bValue: seg1.b,
      type: declineType
    });
  }

  if (header.includes("Df")) {
    if (seg3) {
      seg2.df = convertDeclineToNominal({
        declineValuePercent: val,
        bValue: seg2.b,
        type: declineType
      });
    } else {
      seg1.df = convertDeclineToNominal({
        declineValuePercent: val,
        bValue: seg1.b,
        type: declineType
      });
    }
  }

  if (header === "Start Date") {
    const date = val as Date;
    if (seg0) {
      // Update start date for initial segment will also affect first segment start date
      const months = avgMonthsDiff(
        getDateStringAtMidnightUtc(seg0.startDate),
        seg0.endDate
      );

      // Reset the time to midnight UTC to avoid time zone offsets affecting calculations
      seg0.startDate = getDateStringAtMidnightUtc(date.toISOString());

      //remove the time segment because users cant set time
      seg0.endDate = addAvgFractionalMonths(seg0.startDate, months);
      seg1.startDate = seg0.endDate;
      //need to recalculate di because the number of days between the months could have changed
      updateRampUpSegmentValues(seg0, hasRampUpSegment);
    } else {
      seg1.startDate = date.toISOString();
    }

    updateEndDates(segments);
  }

  if (header === "B" || header === "Btrans") {
    if (declineType === "Secant") {
      seg1.di = convertDeclineToNominal({
        declineValuePercent: convertNominalToDeclineType({
          nominalDeclineValueDecimal: seg1.di,
          bValue: seg1.b,
          type: declineType
        }),
        bValue: val,
        type: declineType
      });
      seg1.df = convertDeclineToNominal({
        declineValuePercent: convertNominalToDeclineType({
          nominalDeclineValueDecimal: seg1.df,
          bValue: seg1.b,
          type: declineType
        }),
        bValue: val,
        type: declineType
      });
    }
    seg1.b = val;
  }

  if (header === "Bhyp") {
    const updatingSeg = is2Segments ? seg1 : seg2;
    if (declineType === "Secant") {
      updatingSeg.di = convertDeclineToNominal({
        declineValuePercent: convertNominalToDeclineType({
          nominalDeclineValueDecimal: updatingSeg.di,
          bValue: updatingSeg.b,
          type: declineType
        }),
        bValue: val,
        type: declineType
      });
      updatingSeg.df = convertDeclineToNominal({
        declineValuePercent: convertNominalToDeclineType({
          nominalDeclineValueDecimal: updatingSeg.df,
          bValue: updatingSeg.b,
          type: declineType
        }),
        bValue: val,
        type: declineType
      });
    }
    updatingSeg.b = val;
  }

  if (header === "Bf") {
    const updatingSeg = is2Segments ? seg2 : seg3;
    updatingSeg.b = val;
  }

  if (header === "Qf") {
    const lastSeg = segments[segments.length - 1];
    lastSeg.qf = val;
  }

  if (header === "TotalEUR") {
    seg1.switchMonth = getDurationInMonths(
      new Date(seg1.startDate),
      new Date(seg1.endDate)
    );
    const product = segments[0].product;
    const cumulativeToDateProduction = cumulativeData?.[product.toLowerCase()] ?? 0;
    const lastProdDate = cumulativeData?.lastProductionDate
      ? BigInt(Math.round(new Date(cumulativeData.lastProductionDate).getTime() / 1000))
      : BigInt(0);
    const di = arpsWasm.getDiFromCumAndRemaining(
      segments.map(arpsWasm.arpSegmentToSegmentDto),
      val * 1000.0,
      cumulativeToDateProduction * 1000.0,
      lastProdDate,
      hasInitialSegment,
      seg1.switchMonth
    );

    if (di > 0) {
      seg1.di = di;
    } else {
      seg1.di = null;
    }
  }

  if (header === ArpFields.EUR) {
    if (hasInitialSegment && seg0) {
      //set timeCalculated to false to use end date
      seg0.timeCalculated = false;
    }
    seg1.switchMonth = getDurationInMonths(
      new Date(seg1.startDate),
      new Date(seg1.endDate)
    );
    const di = arpsWasm.getDiFromCumAndRemaining(
      segments.map(arpsWasm.arpSegmentToSegmentDto),
      val * 1000.0,
      0,
      BigInt(Math.round(new Date("1000-01-01").getTime() / 1000)),
      //start at 1000 to ensure that forecast doesn't get trumped by historical
      hasInitialSegment,
      seg1.switchMonth
    );

    if (di > 0) {
      seg1.di = di;
    } else {
      seg1.di = null;
    }
  }
}

export const updateOneSegmentTerminalDecline: UpdateSegment = ({
  segmentTemplateName,
  productArps,
  declineType,
  header,
  val,
  cumulativeData
}): ArpSegment[] => {
  if (!arpsWasm) {
    throw "arpsWasm is undefined";
  }

  const clone = productArps.map((item) => ({ ...item }));
  const seg1 = clone[0];

  if (clone.length !== 1) {
    throw "The selected type well has an invalid number of segments. Please verify and try again.";
  }

  updateSegmentsFromHeaderValues({
    header,
    segmentTemplateName,
    segments: clone,
    declineType,
    val,
    cumulativeData
  });
  seg1.df = arpsWasm.getNominalDeclineAtRate(seg1.di, seg1.qi, seg1.b, seg1.qf);
  if (header !== "Qf") {
    const productNames = productArps.map((a) =>
      a.product ? a.product.replace(/\s+/g, "") : a.product
    );
    const productEnums = productNames ? ProductTypeEnumAllCaps[productNames[0]] : 0;
    if (!productEnums) {
      throw "invalid product";
    }
  }
  updateEndDates(clone);
  updateTrendCum(clone);
  return clone;
};

export const updateTwoSegmentTerminalDecline: UpdateSegment = ({
  segmentTemplateName,
  productArps,
  declineType,
  header,
  val,
  cumulativeData
}): ArpSegment[] => {
  if (!arpsWasm) {
    throw "arpsWasm is undefined";
  }

  const clone = productArps.map((item) => ({ ...item }));

  if (clone.length !== 2) {
    throw "The selected type well has an invalid number of segments. Please verify and try again.";
  }

  const seg1 = clone[0];
  const seg2 = clone[1];
  updateSegmentsFromHeaderValues({
    header,
    segmentTemplateName,
    segments: clone,
    declineType,
    val,
    cumulativeData
  });

  //these are the calculated fields
  seg1.qf = roundTo(
    arpsWasm.getRateAtNominal(seg1.di, seg1.df, seg1.qi, seg1.b),
    RATE_DECIMAL_PLACES
  );

  // Constrain segment 1's Qf to not decline below segment 2's Qf
  // This prevents segment 1 from calculating past the user's intended final rate
  if (seg2.qf && seg1.qf < seg2.qf) {
    seg1.qf = seg2.qf;
  }

  //di is linked to seg1
  seg2.di = seg1.df;
  seg2.qi = seg1.qf;
  seg2.df = arpsWasm.getNominalDeclineAtRate(seg2.di, seg2.qi, seg2.b, seg2.qf);

  updateEndDates(clone);
  updateTrendCum(clone);

  return clone;
};

export const updateTwoSegmentTerminalDeclineWithRampUp: UpdateSegment = ({
  segmentTemplateName,
  productArps,
  declineType,
  header,
  val,
  cumulativeData
}): ArpSegment[] => {
  if (!arpsWasm) {
    throw "arpsWasm is undefined";
  }

  const clone = productArps.map((item) => ({ ...item }));

  if (clone.length !== 3) {
    throw "The selected type well has an invalid number of segments. Please verify and try again.";
  }

  // Ignore clone[0] which is ramp-up segment.
  const seg2 = clone[1];
  const seg3 = clone[2];
  updateSegmentsFromHeaderValues({
    header,
    segmentTemplateName,
    segments: clone,
    declineType,
    val,
    cumulativeData
  });

  //segment 1 is set by user so only segment 2 an 3 are calculated
  seg2.qf = roundTo(
    arpsWasm.getRateAtNominal(seg2.di, seg2.df, seg2.qi, seg2.b),
    RATE_DECIMAL_PLACES
  );

  // Constrain segment 2's Qf to not decline below segment 3's Qf
  // This prevents segment 2 from calculating past the user's intended final rate
  if (seg3.qf && seg2.qf < seg3.qf) {
    seg2.qf = seg3.qf;
  }

  //di is linked to seg1
  seg3.di = seg2.df;
  seg3.qi = seg2.qf;
  seg3.df = arpsWasm.getNominalDeclineAtRate(seg3.di, seg3.qi, seg3.b, seg3.qf);

  updateEndDates([seg2, seg3]);
  updateTrendCum(clone);
  return clone;
};

export const updateThreeSegmentTerminalDecline: UpdateSegment = ({
  segmentTemplateName,
  productArps,
  declineType,
  header,
  val,
  cumulativeData
}): ArpSegment[] => {
  if (!arpsWasm) {
    throw "arpsWasm is undefined";
  }

  const clone = productArps.map((item) => ({ ...item }));

  if (clone.length !== 3) {
    throw "The selected type well has an invalid number of segments. Please verify and try again.";
  }

  const seg1 = clone[0];
  const seg2 = clone[1];
  const seg3 = clone[2];
  updateSegmentsFromHeaderValues({
    header,
    segmentTemplateName,
    segments: clone,
    declineType,
    val,
    cumulativeData
  });

  const switchMonth = getDurationInDays(seg1.startDate, seg1.endDate);
  seg1.qf = roundTo(
    arpsWasm.getRateAtDay(seg1.qi, seg1.di, seg1.b, switchMonth),
    RATE_DECIMAL_PLACES
  );
  seg1.df = arpsWasm.getNominalDeclineAtRate(seg1.di, seg1.qi, seg1.b, seg1.qf);

  seg2.qi = seg1.qf;
  seg2.di = seg1.df;
  seg2.qf = roundTo(
    arpsWasm.getRateAtNominal(seg2.di, seg2.df, seg2.qi, seg2.b),
    RATE_DECIMAL_PLACES
  );

  // Constrain segment 2's Qf to not decline below segment 3's Qf
  // This prevents segment 2 from calculating past the user's intended final rate
  if (seg3.qf && seg2.qf < seg3.qf) {
    seg2.qf = seg3.qf;
  }

  seg3.qi = seg2.qf;
  seg3.di = seg2.df;
  seg3.df = arpsWasm.getNominalDeclineAtRate(seg3.di, seg3.qi, seg3.b, seg2.qf);

  updateEndDates(clone);
  updateTrendCum(clone);
  return clone;
};

export const updateThreeSegmentTerminalDeclineWithRampUp: UpdateSegment = ({
  segmentTemplateName,
  productArps,
  declineType,
  header,
  val,
  cumulativeData
}): ArpSegment[] => {
  if (!arpsWasm) {
    throw "arpsWasm is undefined";
  }
  const clone = productArps.map((item) => ({ ...item }));

  if (clone.length !== 4) {
    throw "The selected type well has an invalid number of segments. Please verify and try again.";
  }

  // Ignore clone[0] which is the ramp-up segment.
  const seg2 = clone[1];
  const seg3 = clone[2];
  const seg4 = clone[3];
  updateSegmentsFromHeaderValues({
    header,
    segmentTemplateName,
    segments: clone,
    declineType,
    val,
    cumulativeData
  });

  const switchMonth = getDurationInDays(seg2.startDate, seg2.endDate);
  seg2.qf = roundTo(
    arpsWasm.getRateAtDay(seg2.qi, seg2.di, seg2.b, switchMonth),
    RATE_DECIMAL_PLACES
  );
  seg2.df = arpsWasm.getNominalDeclineAtRate(seg2.di, seg2.qi, seg2.b, seg2.qf);

  seg3.qi = seg2.qf;
  seg3.di = seg2.df;
  seg3.qf = roundTo(
    arpsWasm.getRateAtNominal(seg3.di, seg3.df, seg3.qi, seg3.b),
    RATE_DECIMAL_PLACES
  );

  // Constrain segment 3's Qf to not decline below segment 4's Qf
  // This prevents segment 3 from calculating past the user's intended final rate
  if (seg4.qf && seg3.qf < seg4.qf) {
    seg3.qf = seg4.qf;
  }

  // Cascading constraint: also constrain segment 2's Qf to not go below segment 3's Qf
  // This handles the case where increasing final Qf makes seg3 constrained but seg2 calculates lower
  if (seg3.qf && seg2.qf < seg3.qf) {
    seg2.qf = seg3.qf;
  }

  seg4.qi = seg3.qf;
  seg4.di = seg3.df;
  seg4.df = arpsWasm.getNominalDeclineAtRate(seg4.di, seg4.qi, seg4.b, seg4.qf);

  updateEndDates([seg2, seg3, seg4]);
  updateTrendCum(clone);
  return clone;
};

export const updateThreeSegmentTerminalDeclineWithConstrainedPeriod: UpdateSegment = ({
  segmentTemplateName,
  productArps,
  declineType,
  header,
  val,
  cumulativeData
}): ArpSegment[] => {
  if (!arpsWasm) {
    throw "arpsWasm is undefined";
  }

  const clone = productArps.map((item) => ({ ...item }));

  if (clone.length !== 4) {
    throw "The selected type well has an invalid number of segments. Please verify and try again.";
  }

  const seg1 = clone[0];
  const seg2 = clone[1];
  const seg3 = clone[2];
  const seg4 = clone[3];
  updateSegmentsFromHeaderValues({
    header,
    segmentTemplateName,
    segments: clone,
    declineType,
    val,
    cumulativeData
  });

  const isNoDecline = seg1.qi === seg1.qf;
  if (isNoDecline) {
    seg1.di = 0;
    seg1.df = 0; // Flat segment has no decline, so df should also be 0
  }

  const switchMonth = getDurationInDays(seg2.startDate, seg2.endDate);
  seg2.qf = roundTo(
    arpsWasm.getRateAtDay(seg2.qi, seg2.di, seg2.b, switchMonth),
    RATE_DECIMAL_PLACES
  );
  seg2.df = arpsWasm.getNominalDeclineAtRate(seg2.di, seg2.qi, seg2.b, seg2.qf);

  seg3.qi = seg2.qf;
  seg3.di = seg2.df;
  seg3.qf = roundTo(
    arpsWasm.getRateAtNominal(seg3.di, seg3.df, seg3.qi, seg3.b),
    RATE_DECIMAL_PLACES
  );

  // Constrain segment 3's Qf to not decline below segment 4's Qf
  // This prevents segment 3 from calculating past the user's intended final rate
  if (seg4.qf && seg3.qf < seg4.qf) {
    seg3.qf = seg4.qf;
  }

  // Cascading constraint: also constrain segment 2's Qf to not go below segment 3's Qf
  // This handles the case where increasing final Qf makes seg3 constrained but seg2 calculates lower
  if (seg3.qf && seg2.qf < seg3.qf) {
    seg2.qf = seg3.qf;
  }

  seg4.qi = seg3.qf;
  seg4.di = seg3.df;
  seg4.df = arpsWasm.getNominalDeclineAtRate(seg4.di, seg4.qi, seg4.b, seg4.qf);

  updateEndDates([seg2, seg3, seg4]);
  updateTrendCum(clone);
  return clone;
};

/**
 * SAGD template update function
 *
 * Supports two modes:
 * 1. Two-segment mode (tRamp = 0): Flat production + Exponential decline
 * 2. Three-segment mode (tRamp > 0): Linear ramp-up + Flat production + Exponential decline
 *
 * Auto-restructures between modes based on tRamp value changes.
 */
export const updateSAGD: UpdateSegment = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  segmentTemplateName,
  productArps,
  declineType,
  header,
  val
}): ArpSegment[] => {
  if (!arpsWasm) {
    throw "arpsWasm is undefined";
  }

  const clone = productArps.map((item) => ({ ...item }));
  const numSegments = clone.length;

  // Determine segment indices based on mode
  const hasRampUp = numSegments === 3;
  const rampIdx = hasRampUp ? 0 : -1;
  const flatIdx = hasRampUp ? 1 : 0;
  const declineIdx = hasRampUp ? 2 : 1;

  const rampSeg = hasRampUp ? clone[rampIdx] : null;
  const flatSeg = clone[flatIdx];
  const declineSeg = clone[declineIdx];

  // Helper: Recalculate flat segment end date
  const updateFlatEndDate = (flat: ArpSegment, tFlat: number) => {
    if (flat.startDate) {
      flat.endDate = addAvgFractionalMonths(flat.startDate, tFlat);
    }
  };

  // Helper: Update decline segment start from flat end
  const syncDeclineStart = (flat: ArpSegment, decline: ArpSegment) => {
    decline.startDate = flat.endDate;
    decline.qi = flat.qf;
    // For exponential decline (b=0), di is a fixed parameter - don't recalculate it
    // Only recalculate df based on the new qi
    decline.df = arpsWasm.getNominalDeclineAtRate(
      decline.di,
      decline.qi,
      decline.b,
      decline.qf
    );
  };

  if (header.includes(ArpFields.Di)) {
    const nominalDi = convertDeclineToNominal({
      declineValuePercent: val as number,
      bValue: declineSeg.b,
      type: declineType
    });
    declineSeg.di = nominalDi;
    declineSeg.df = arpsWasm.getNominalDeclineAtRate(
      declineSeg.di,
      declineSeg.qi,
      declineSeg.b,
      declineSeg.qf
    );
    updateEndDates([declineSeg]);
    updateTrendCum(clone);
    return clone;
  }

  if (header.includes(ArpFields.Df)) {
    const nominalDf = convertDeclineToNominal({
      declineValuePercent: val as number,
      bValue: declineSeg.b,
      type: declineType
    });
    declineSeg.df = nominalDf;
    updateEndDates([declineSeg]);
    updateTrendCum(clone);
    return clone;
  }

  // Handle field-specific updates
  switch (header) {
    case ArpFields.TRamp: {
      const tRamp = val as number;
      if (tRamp > 0 && !hasRampUp) {
        // Transition from 2-segment to 3-segment: insert ramp-up
        const currentTFlat = avgMonthsDiff(flatSeg.startDate, flatSeg.endDate);
        const preservedTFlat =
          currentTFlat && currentTFlat > 0 && isFinite(currentTFlat) ? currentTFlat : 12;

        const rampEndDate = flatSeg.startDate
          ? addAvgFractionalMonths(flatSeg.startDate, tRamp)
          : new Date().toISOString();
        const newRampSeg: ArpSegment = {
          product: flatSeg.product,
          segmentIndex: 0,
          b: -1.0, // Harmonic ramp
          di: 0, // Will be calculated by updateRampUpSegmentValues
          df: 0,
          qi: 0, // Will be set by Q0
          qf: flatSeg.qi, // Ends at flat Qi
          startDate: flatSeg.startDate,
          endDate: rampEndDate,
          trendCum: 0,
          maxRate: flatSeg.maxRate
        };
        // Calculate harmonic ramp (b=-1) for SAGD
        updateRampUpSegmentValues(newRampSeg, true);
        // Shift existing segments
        flatSeg.segmentIndex = 1;
        flatSeg.startDate = newRampSeg.endDate;
        // Preserve tFlat by recalculating the flat segment's end date
        flatSeg.endDate = addAvgFractionalMonths(flatSeg.startDate, preservedTFlat);
        declineSeg.segmentIndex = 2;
        clone.unshift(newRampSeg);
        // Sync decline segment start with flat end and update end dates
        syncDeclineStart(flatSeg, declineSeg);
        updateEndDates([declineSeg]);
      } else if (tRamp === 0 && hasRampUp && rampSeg) {
        // Transition from 3-segment to 2-segment: remove ramp-up
        clone.shift();
        clone[0].segmentIndex = 0;
        clone[0].startDate = rampSeg.startDate;
        clone[1].segmentIndex = 1;
        updateEndDates(clone);
      } else if (tRamp > 0 && hasRampUp && rampSeg && rampSeg.startDate) {
        // Capture tFlat BEFORE changing any dates
        const currentTFlat =
          flatSeg.startDate && flatSeg.endDate
            ? avgMonthsDiff(flatSeg.startDate, flatSeg.endDate)
            : DEFAULT_TFLAT;

        // Update ramp duration
        rampSeg.endDate = addAvgFractionalMonths(rampSeg.startDate, tRamp);

        updateRampUpSegmentValues(rampSeg, true);
        flatSeg.startDate = rampSeg.endDate;
        // Preserve tFlat by recalculating the flat segment's end date
        updateFlatEndDate(flatSeg, currentTFlat);
        syncDeclineStart(flatSeg, declineSeg);
        updateEndDates([declineSeg]);
      }
      break;
    }

    case ArpFields.TFlat: {
      const tFlat = val as number;
      updateFlatEndDate(flatSeg, tFlat);
      syncDeclineStart(flatSeg, declineSeg);
      updateEndDates([declineSeg]);
      break;
    }

    case ArpFields.Q0: {
      const newQ0 = val as number;
      if (hasRampUp && rampSeg) {
        // Update existing ramp segment's starting rate
        rampSeg.qi = newQ0;
        // Recalculate harmonic ramp (b=-1) for SAGD
        updateRampUpSegmentValues(rampSeg, true);
      } else if (!hasRampUp && newQ0 > 0) {
        // Transition from 2-segment to 3-segment: Q0 entered triggers ramp creation
        const currentTFlat = avgMonthsDiff(flatSeg.startDate, flatSeg.endDate);
        const preservedTFlat =
          currentTFlat && currentTFlat > 0 && isFinite(currentTFlat)
            ? currentTFlat
            : DEFAULT_TFLAT;

        const rampEndDate = flatSeg.startDate
          ? addAvgFractionalMonths(flatSeg.startDate, DEFAULT_TFLAT)
          : new Date().toISOString();
        const newRampSeg: ArpSegment = {
          product: flatSeg.product,
          segmentIndex: 0,
          b: -1.0, // Harmonic ramp
          di: 0, // Will be calculated by updateRampUpSegmentValues
          df: 0,
          qi: newQ0, // Use the entered Q0 value
          qf: flatSeg.qi, // Ends at flat Qi
          startDate: flatSeg.startDate,
          endDate: rampEndDate,
          trendCum: 0,
          maxRate: flatSeg.maxRate
        };
        // Calculate harmonic ramp (b=-1) for SAGD
        updateRampUpSegmentValues(newRampSeg, true);
        // Shift existing segments
        flatSeg.segmentIndex = 1;
        flatSeg.startDate = newRampSeg.endDate;
        // Preserve tFlat by recalculating the flat segment's end date
        flatSeg.endDate = addAvgFractionalMonths(flatSeg.startDate, preservedTFlat);
        declineSeg.segmentIndex = 2;
        clone.unshift(newRampSeg);
        // Sync decline segment start with flat end and update end dates
        syncDeclineStart(flatSeg, declineSeg);
        updateEndDates([declineSeg]);
      }
      break;
    }

    case ArpFields.Qi: {
      const newQi = val as number;
      // Qi is the peak rate (end of ramp / start of flat)
      if (hasRampUp && rampSeg) {
        rampSeg.qf = newQi;
      }
      flatSeg.qi = newQi;
      flatSeg.qf = newQi;
      declineSeg.qi = newQi;
      // Recalculate df based on the new qi
      declineSeg.df = arpsWasm.getNominalDeclineAtRate(
        declineSeg.di,
        declineSeg.qi,
        declineSeg.b,
        declineSeg.qf
      );
      updateEndDates([declineSeg]);
      break;
    }

    case ArpFields.Qf: {
      declineSeg.qf = val as number;
      declineSeg.df = arpsWasm.getNominalDeclineAtRate(
        declineSeg.di,
        declineSeg.qi,
        declineSeg.b,
        declineSeg.qf
      );
      updateEndDates([declineSeg]);
      break;
    }

    case ArpFields.StartDate: {
      const date = val as Date;
      const newStart = getDateStringAtMidnightUtc(date.toISOString());

      // Capture tFlat BEFORE changing any dates (so we preserve the duration)
      const currentTFlat =
        flatSeg.startDate && flatSeg.endDate
          ? avgMonthsDiff(flatSeg.startDate, flatSeg.endDate)
          : DEFAULT_TFLAT;

      if (hasRampUp && rampSeg && rampSeg.startDate && rampSeg.endDate) {
        // Preserve tRamp duration when moving start date
        const tRamp = avgMonthsDiff(rampSeg.startDate, rampSeg.endDate);
        rampSeg.startDate = newStart;
        rampSeg.endDate = addAvgFractionalMonths(newStart, tRamp);
        // Recalculate harmonic ramp values after date change
        updateRampUpSegmentValues(rampSeg, true);
        flatSeg.startDate = rampSeg.endDate;
      } else {
        flatSeg.startDate = newStart;
      }
      // Update flat end date using the preserved tFlat
      updateFlatEndDate(flatSeg, currentTFlat);
      syncDeclineStart(flatSeg, declineSeg);
      updateEndDates([declineSeg]);
      break;
    }

    case ArpFields.MaxRate: {
      // maxRate is same as Qi for SAGD
      const newMaxRate = val as number;
      if (hasRampUp && rampSeg) {
        rampSeg.qf = newMaxRate;
      }
      flatSeg.qi = newMaxRate;
      flatSeg.qf = newMaxRate;
      declineSeg.qi = newMaxRate;
      // For exponential decline (b=0), di is a fixed parameter - don't recalculate it
      // Recalculate df based on the new qi
      declineSeg.df = arpsWasm.getNominalDeclineAtRate(
        declineSeg.di,
        declineSeg.qi,
        declineSeg.b,
        declineSeg.qf
      );
      updateEndDates([declineSeg]);
      break;
    }

    case ArpFields.EUR: {
      // Adjust Qf to achieve target EUR
      const targetEUR = val as number;
      const currentEUR = clone.reduce((sum, seg) => sum + (seg.trendCum || 0), 0);
      if (currentEUR > 0) {
        const ratio = targetEUR / currentEUR;
        declineSeg.qf = roundTo(declineSeg.qf * ratio, RATE_DECIMAL_PLACES);
        declineSeg.df = arpsWasm.getNominalDeclineAtRate(
          declineSeg.di,
          declineSeg.qi,
          declineSeg.b,
          declineSeg.qf
        );
        updateEndDates([declineSeg]);
      }
      break;
    }

    default:
      break;
  }

  updateTrendCum(clone);
  return clone;
};
